import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import api from '../services/api';
import { 
  ChartBarIcon,
  UserIcon,
  ClockIcon,
  CalendarIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowTrendingUpIcon,
  TrashIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

const AdminAnalytics = () => {
  const [activeTab, setActiveTab] = useState('page-views');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [page, setPage] = useState(1);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState('page_views');
  const [deleteDays, setDeleteDays] = useState(30);

  // 페이지별 접속 기록
  const { data: pageViewsData, isLoading: pageViewsLoading } = useQuery(
    ['page-views', selectedDate, page, selectedUserId],
    () => api.get('/analytics/page-views', {
      params: {
        date: selectedDate,
        page,
        limit: 50,
        ...(selectedUserId && { user_id: selectedUserId })
      }
    }).then(res => res.data),
    {
      keepPreviousData: true,
      enabled: activeTab === 'page-views'
    }
  );

  // 페이지별 통계
  const { data: pageStatsData, isLoading: pageStatsLoading } = useQuery(
    ['page-stats', selectedDate],
    () => api.get('/analytics/page-stats', {
      params: {
        date_from: selectedDate,
        date_to: selectedDate
      }
    }).then(res => res.data),
    {
      enabled: activeTab === 'page-stats'
    }
  );

  // 사용자별 통계
  const { data: userStatsData, isLoading: userStatsLoading } = useQuery(
    ['user-stats', selectedDate],
    () => api.get('/analytics/user-stats', {
      params: {
        date_from: selectedDate,
        date_to: selectedDate
      }
    }).then(res => res.data),
    {
      enabled: activeTab === 'user-stats'
    }
  );

  const formatDuration = (seconds) => {
    if (!seconds || seconds < 0) return '0초';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}시간 ${minutes}분`;
    } else if (minutes > 0) {
      return `${minutes}분 ${secs}초`;
    } else {
      return `${secs}초`;
    }
  };

  const getPageTitle = (url) => {
    const titles = {
      '/': '홈',
      '/projects': '프로젝트 목록',
      '/chat': '채팅',
      '/profile': '프로필',
      '/vip': 'VIP',
      '/help': '도움말',
      '/notices': '공지사항'
    };
    
    // 동적 URL 처리
    if (url.startsWith('/projects/')) return '프로젝트 상세';
    if (url.startsWith('/chat/')) return '채팅방';
    
    return titles[url] || url;
  };

  const getUserTypeLabel = (type) => {
    const labels = {
      admin: '관리자',
      contractor: '소장',
      customer: '고객',
      vip_customer: 'VIP 고객'
    };
    return labels[type] || type;
  };

  const getUserTypeBadgeColor = (type) => {
    const colors = {
      admin: 'bg-red-100 text-red-800',
      contractor: 'bg-blue-100 text-blue-800',
      customer: 'bg-green-100 text-green-800',
      vip_customer: 'bg-yellow-100 text-yellow-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const handleDeleteOldData = async () => {
    try {
      console.log('Deleting data:', { days: deleteDays, type: deleteType });
      const response = await api.delete('/analytics/cleanup', {
        data: {
          days: deleteDays,
          type: deleteType
        }
      });
      console.log('Delete response:', response.data);
      
      toast.success(`${deleteDays}일 이전 ${deleteType === 'page_views' ? '페이지 접속 기록' : deleteType === 'analytics_data' ? '분석 데이터' : '모든 분석 데이터'}이 삭제되었습니다.`);
      setShowDeleteModal(false);
      // 데이터 새로고침
      window.location.reload();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(`데이터 삭제에 실패했습니다: ${error.response?.data?.error || error.message}`);
    }
  };

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">접속 분석</h2>
          <p className="text-sm text-gray-600 mt-1">사용자 접속 패턴과 페이지별 체류 시간을 분석합니다</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowDeleteModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <TrashIcon className="h-5 w-5" />
            데이터 정리
          </button>
          <CalendarIcon className="h-5 w-5 text-gray-400" />
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => {
              setSelectedDate(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* 탭 메뉴 */}
      <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
        <button
          onClick={() => setActiveTab('page-views')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'page-views'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          실시간 접속 기록
        </button>
        <button
          onClick={() => setActiveTab('page-stats')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'page-stats'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          페이지별 통계
        </button>
        <button
          onClick={() => setActiveTab('user-stats')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'user-stats'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          사용자별 통계
        </button>
      </div>

      {/* 실시간 접속 기록 */}
      {activeTab === 'page-views' && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {pageViewsLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : (
            <>
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      시간
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      사용자
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      페이지
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      체류 시간
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      디바이스
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pageViewsData?.views?.map((view) => (
                    <tr key={view.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {format(new Date(view.enter_time), 'HH:mm:ss', { locale: ko })}
                        {view.leave_time && (
                          <span className="text-gray-500">
                            {' → '}
                            {format(new Date(view.leave_time), 'HH:mm:ss', { locale: ko })}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {view.user_name}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getUserTypeBadgeColor(view.user_type)}`}>
                              {getUserTypeLabel(view.user_type)}
                            </span>
                            {view.company_name && (
                              <span className="text-xs text-gray-500">{view.company_name}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {getPageTitle(view.page_url)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {view.page_url}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {view.duration_seconds > 0 ? (
                          <span className="font-medium text-gray-900">
                            {formatDuration(view.duration_seconds)}
                          </span>
                        ) : (
                          <span className="text-gray-400">진행중...</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          {view.device_type === 'mobile' ? (
                            <DevicePhoneMobileIcon className="h-4 w-4 text-gray-400" />
                          ) : (
                            <ComputerDesktopIcon className="h-4 w-4 text-gray-400" />
                          )}
                          <span className="text-sm text-gray-500 capitalize">
                            {view.device_type}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* 페이지네이션 */}
              {pageViewsData?.pages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        <span className="font-medium">{((page - 1) * 50) + 1}</span>-<span className="font-medium">{Math.min(page * 50, pageViewsData.total)}</span> / 
                        총 <span className="font-medium">{pageViewsData.total}</span>개
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* 첫 페이지 */}
                      <button
                        onClick={() => setPage(1)}
                        disabled={page === 1}
                        className="relative inline-flex items-center px-3 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        처음
                      </button>
                      
                      {/* 이전 페이지 */}
                      <button
                        onClick={() => setPage(prev => Math.max(1, prev - 10))}
                        disabled={page <= 10}
                        className="relative inline-flex items-center px-2 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="10페이지 이전"
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                        <ChevronLeftIcon className="h-4 w-4 -ml-1" />
                      </button>
                      
                      <button
                        onClick={() => setPage(prev => Math.max(1, prev - 1))}
                        disabled={page === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                      </button>

                      {/* 페이지 번호들 */}
                      <div className="flex items-center">
                        {(() => {
                          const totalPages = pageViewsData.pages;
                          const current = page;
                          const range = 2; // 현재 페이지 좌우로 보여줄 페이지 수
                          const pages = [];
                          
                          let start = Math.max(1, current - range);
                          let end = Math.min(totalPages, current + range);
                          
                          // 시작 부분 조정
                          if (end - start < range * 2) {
                            if (start === 1) {
                              end = Math.min(totalPages, start + range * 2);
                            } else {
                              start = Math.max(1, end - range * 2);
                            }
                          }
                          
                          // 첫 페이지와 간격이 있으면 1과 ... 표시
                          if (start > 1) {
                            pages.push(
                              <button
                                key={1}
                                onClick={() => setPage(1)}
                                className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 border border-gray-300 rounded-md"
                              >
                                1
                              </button>
                            );
                            if (start > 2) {
                              pages.push(
                                <span key="start-ellipsis" className="px-2 py-2 text-sm text-gray-500">...</span>
                              );
                            }
                          }
                          
                          // 메인 페이지 번호들
                          for (let i = start; i <= end; i++) {
                            pages.push(
                              <button
                                key={i}
                                onClick={() => setPage(i)}
                                className={`relative inline-flex items-center px-3 py-2 text-sm font-medium border rounded-md ${
                                  i === current
                                    ? 'bg-blue-600 text-white border-blue-600 cursor-default'
                                    : 'text-gray-700 hover:bg-gray-50 border-gray-300'
                                }`}
                              >
                                {i}
                              </button>
                            );
                          }
                          
                          // 마지막 페이지와 간격이 있으면 ... 과 마지막 페이지 표시
                          if (end < totalPages) {
                            if (end < totalPages - 1) {
                              pages.push(
                                <span key="end-ellipsis" className="px-2 py-2 text-sm text-gray-500">...</span>
                              );
                            }
                            pages.push(
                              <button
                                key={totalPages}
                                onClick={() => setPage(totalPages)}
                                className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 border border-gray-300 rounded-md"
                              >
                                {totalPages}
                              </button>
                            );
                          }
                          
                          return <div className="flex items-center gap-1">{pages}</div>;
                        })()}
                      </div>

                      {/* 다음 페이지 */}
                      <button
                        onClick={() => setPage(prev => Math.min(pageViewsData.pages, prev + 1))}
                        disabled={page === pageViewsData.pages}
                        className="relative inline-flex items-center px-2 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRightIcon className="h-4 w-4" />
                      </button>
                      
                      <button
                        onClick={() => setPage(prev => Math.min(pageViewsData.pages, prev + 10))}
                        disabled={page > pageViewsData.pages - 10}
                        className="relative inline-flex items-center px-2 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="10페이지 다음"
                      >
                        <ChevronRightIcon className="h-4 w-4" />
                        <ChevronRightIcon className="h-4 w-4 -ml-1" />
                      </button>

                      {/* 마지막 페이지 */}
                      <button
                        onClick={() => setPage(pageViewsData.pages)}
                        disabled={page === pageViewsData.pages}
                        className="relative inline-flex items-center px-3 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        마지막
                      </button>
                      
                      {/* 페이지 직접 입력 */}
                      <div className="flex items-center gap-1 ml-2">
                        <input
                          type="number"
                          min="1"
                          max={pageViewsData.pages}
                          value={page}
                          onChange={(e) => {
                            const newPage = parseInt(e.target.value);
                            if (newPage >= 1 && newPage <= pageViewsData.pages) {
                              setPage(newPage);
                            }
                          }}
                          className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                          placeholder="페이지"
                        />
                        <span className="text-sm text-gray-500">페이지</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* 페이지별 통계 */}
      {activeTab === 'page-stats' && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {pageStatsLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : (
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    페이지
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    방문 횟수
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    평균 체류 시간
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    최대 체류 시간
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    순 방문자
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pageStatsData?.map((stat) => (
                  <tr key={stat.page_url} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {getPageTitle(stat.page_url)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {stat.page_url}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">
                          {stat.visit_count}회
                        </span>
                        <ArrowTrendingUpIcon className="h-4 w-4 text-green-500" />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDuration(Math.round(stat.avg_duration))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDuration(stat.max_duration)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {stat.unique_visitors}명
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* 사용자별 통계 */}
      {activeTab === 'user-stats' && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {userStatsLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : (
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    사용자
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    페이지 조회
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    총 체류 시간
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    평균 체류 시간
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    마지막 활동
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {userStatsData?.map((stat) => (
                  <tr 
                    key={stat.user_id} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => {
                      setSelectedUserId(stat.user_id);
                      setActiveTab('page-views');
                      setPage(1);
                    }}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {stat.user_name}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getUserTypeBadgeColor(stat.user_type)}`}>
                            {getUserTypeLabel(stat.user_type)}
                          </span>
                          {stat.company_name && (
                            <span className="text-xs text-gray-500">{stat.company_name}</span>
                          )}
                          {stat.phone && (
                            <span className="text-xs text-gray-400">{stat.phone}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">
                        {stat.total_page_views}회
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDuration(stat.total_duration)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDuration(Math.round(stat.avg_duration))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {stat.last_active ? 
                        format(new Date(stat.last_active), 'MM/dd HH:mm', { locale: ko }) : 
                        '-'
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* 데이터 삭제 모달 */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              <h3 className="text-lg font-bold">오래된 데이터 삭제</h3>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  삭제할 데이터 유형
                </label>
                <select
                  value={deleteType}
                  onChange={(e) => setDeleteType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="page_views">페이지 접속 기록</option>
                  <option value="analytics_data">분석 데이터</option>
                  <option value="all_analytics">모든 분석 데이터</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  보관 기간
                </label>
                <select
                  value={deleteDays}
                  onChange={(e) => setDeleteDays(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value={1}>1일 이전 데이터 삭제</option>
                  <option value={3}>3일 이전 데이터 삭제</option>
                  <option value={7}>7일 이전 데이터 삭제</option>
                  <option value={15}>15일 이전 데이터 삭제</option>
                  <option value={30}>30일 이전 데이터 삭제</option>
                  <option value={60}>60일 이전 데이터 삭제</option>
                  <option value={90}>90일 이전 데이터 삭제</option>
                  <option value={180}>180일 이전 데이터 삭제</option>
                  <option value={365}>365일 이전 데이터 삭제</option>
                </select>
              </div>
              <p className="text-sm text-gray-600">
                선택한 기간보다 오래된 {deleteType === 'page_views' ? '페이지 접속 기록' : deleteType === 'analytics_data' ? '분석 데이터' : '모든 분석 데이터'}가 
                영구적으로 삭제됩니다. 이 작업은 되돌릴 수 없습니다.
              </p>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                취소
              </button>
              <button
                onClick={handleDeleteOldData}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAnalytics;